import { useEffect, useState } from 'react'

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    TextField,
    Autocomplete,
    Typography
} from '@mui/material'

import axios from 'axios'
import Swal from 'sweetalert2'

import apiUrl from '../../../src/configs/constant'
import { getGudangJadi } from '../../api/helper'

interface Props {
    open: boolean
    onClose: () => void
    onSuccess: () => void
    token: string
    mmgudang_id: any
    wo_order_id?: any
}

export default function DialogTerimaHasilProduksi(props: Props) {

    const [saving, setSaving] = useState(false)

    const [woOrder, setWoOrder] = useState<any>(null)
    const [qty, setQty] = useState('')

    const [listWorkOrder, setListWorkOrder] = useState<any[]>([])
    const [listGudang, setListGudang] = useState<any[]>([])

    const [gudang, setGudang] = useState<any>(null)

    // =========================
    // OPEN DIALOG RESET + LOAD
    // =========================
    useEffect(() => {
        if (!props.open) return

        setWoOrder(null)
        setQty('')
        setGudang(null)

        getWorkOrder()
        fetchGudang()
    }, [props.open])

    // =========================
    // FETCH GUDANG
    // =========================
    const fetchGudang = async () => {
        const response = await getGudangJadi(props.token)

        const data = Array.isArray(response)
            ? response
            : response?.data ?? []

        setListGudang(data)

        if (!props.mmgudang_id) return

        const selected = data.find(
            (g: any) => Number(g.id) === Number(props.mmgudang_id)
        )

        setGudang(selected || null)
    }

    // =========================
    // FETCH WO
    // =========================
    const getWorkOrder = () => {
        axios.get(apiUrl.apiUrl + '/production/bhp/list-open-wo', {
            headers: { Authorization: `Bearer ${props.token}` }
        })
            .then(response => {
                if (response.data.success) {
                    const data = response.data.data
                    const selected = data.find(
                        (element: any) => element.id === props.wo_order_id
                    )

                    setWoOrder(selected)
                    setListWorkOrder(data)
                }
            })
            .catch(console.error)
    }

    // =========================
    // FORMAT NUMBER
    // =========================
    const formatNumber = (value: string) => {
        const numeric = value.replace(/[^\d.]/g, '')
        if (!numeric) return ''

        const parts = numeric.split('.')
        parts[0] = Number(parts[0]).toLocaleString('en-US')

        return parts.join('.')
    }

    // =========================
    // SAVE
    // =========================
    const handleSave = async () => {

        if (!woOrder) {
            Swal.fire('Pemberitahuan', 'Work Order harus dipilih', 'warning')
            return
        }

        if (!qty || Number(qty.replace(/,/g, '')) <= 0) {
            Swal.fire('Pemberitahuan', 'Qty harus lebih besar dari 0', 'warning')
            return
        }

        try {
            setSaving(true)

            props.onClose()

            const response = await axios.post(
                apiUrl.apiUrl + '/production/terima-hasil-produksi/add',
                {
                    wo_order_id: woOrder.id,
                    qty: Number(qty.replace(/,/g, '')),
                    sysdoctag_id: 17,
                    mmgudang_id: gudang?.id ?? props.mmgudang_id
                },
                {
                    headers: {
                        Authorization: `Bearer ${props.token}`
                    }
                }
            )

            if (response.data.success) {

                Swal.fire('Berhasil', response.data.message, 'success')

                setWoOrder(null)
                setQty('')

                props.onClose()
                props.onSuccess()

            } else {
                Swal.fire('Pemberitahuan', response.data.message, 'error')
            }

        } catch (error: any) {
            Swal.fire(
                'Pemberitahuan',
                error?.response?.data?.message || error.message,
                'error'
            )
        } finally {
            setSaving(false)
        }
    }

    // =========================
    // UI
    // =========================
    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth='sm'
        >

            <DialogTitle>
                <Typography variant='h6' sx={{ fontWeight: 500 }}>
                    Terima Hasil Produksi
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2}>

                    {/* GUDANG */}
                    <Grid item xs={12}>
                        <Autocomplete
                            options={listGudang}
                            value={gudang}
                            disabled={!!props.mmgudang_id}
                            onChange={(e, val) => setGudang(val)}
                            getOptionLabel={(option: any) => option?.nama || ''}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Gudang'
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* WO */}
                    <Grid item xs={12}>
                        <Autocomplete
                            options={listWorkOrder}
                            value={woOrder}
                            onChange={(e, val) => setWoOrder(val)}
                            getOptionLabel={(option: any) =>
                                `${option.id} - ${option.sku} (${option.jenis})`
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Work Order'
                                    fullWidth
                                />
                            )}
                        />
                    </Grid>

                    {/* INFO WO */}
                    {woOrder && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label='Qty WO'
                                value={woOrder.qty}
                                disabled
                            />
                        </Grid>
                    )}

                    {/* QTY INPUT */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label='Qty Hasil'
                            value={qty}
                            onChange={(e) =>
                                setQty(formatNumber(e.target.value))
                            }
                        />
                    </Grid>

                </Grid>

            </DialogContent>

            <DialogActions>

                <Button onClick={props.onClose} color='inherit'>
                    Batal
                </Button>

                <Button
                    variant='contained'
                    disabled={saving}
                    onClick={handleSave}
                >
                    Simpan
                </Button>

            </DialogActions>

        </Dialog>
    )
}